import { Component, ContentChild, EventEmitter, Input, Output, TemplateRef } from '@angular/core';

import { Observable, BehaviorSubject, combineLatest } from 'rxjs';
import { filter, map, tap, withLatestFrom } from 'rxjs/operators';
import { SubjectRelay } from '../../commands/subject-relay.class';

export type MultiExtendedFilterableSelect2ReinitData = {
	list: any[] | undefined;
	preselectionFunction: ((item: any) => boolean) | undefined;
}

class WrappedItem {
	constructor(
		public index: number, //
		public entity: any, //
		public selected: boolean, // 
	) {
	}
}

var allowLog = false;

var _logTap = <T> (label : string) => tap(
	(data: T) => {
		if (allowLog)
			console.log("mefs2 - " + label + ": ", data);
	}
);

@Component({
	selector: 'multi-extended-filterable-select2',
	templateUrl: 'multi-extended-filterable-select2.html',
	styleUrls: ['multi-extended-filterable-select2.css']
})
export class MultiExtendedFilterableSelectComponent2 {
	// Indicateurs comportementaux pour l'IHM
	showAddOrRemoveAll$!: Observable<boolean>;
	allowAddAll$!: Observable<boolean>;
	allowRemoveAll$!: Observable<boolean>;
	showSelect$!: Observable<boolean>;
	
	// Liste des items generes, encapsules pour faciliter le traitement
	wrappedItems$ = new BehaviorSubject<WrappedItem[]>([]);

	// Liste a binder sur la vue en fonction de la saisie utilisateur
	selectedItems$!: Observable<WrappedItem[]>;
	selectableItems$!: Observable<WrappedItem[]>;
	
	// La fonction a utiliser pour verifier sur un -wrappedItem- appartient a une categorie
	wrappedItemCategoryChecker = (wrappedItem: WrappedItem, category: any) => this.categoryChecker(wrappedItem.entity, category);

	// Fonction a utiliser pour obtenir le libelle d'un item encapsule
	getWrappedItemLabel = (wrappedItem: WrappedItem) => this.labelGetter(wrappedItem.entity);

	// Item actuellement selectionne dans le select
	selectedItem: any = null;
	
	constructor() {
		//////////////////////////////
		// Reduction des evenements //
		//////////////////////////////
		
		/////////////////////////////////
		// Calcul des valeurs derivees //
		/////////////////////////////////
		this.selectedItems$ = this.wrappedItems$.pipe(
			_logTap("wrappedItems$"),
			map(
				(wrappedItems) => {
					let selectedItems = wrappedItems //
						.filter(
							(wrappedItem: WrappedItem) => wrappedItem.selected
						)
					;
					
					return selectedItems;
				}
			),
			_logTap("selectedItems$"),
		);
		
		let unwrappedSelectedItems$ = this.selectedItems$.pipe(
			map(
				(wrappedItems) => wrappedItems.map(
					(wrappeditem: WrappedItem) => wrappeditem.entity
				)
			),
		);
		
		this.selectableItems$ = this.wrappedItems$.pipe(
			map(
				(wrappedItems) => {
					let selectedItems = wrappedItems //
						.filter(
							(wrappedItem: WrappedItem) => !wrappedItem.selected
						)
					;
					
					return selectedItems;
				}
			),
			_logTap("selectableItems$"),
		);
		
		this.showAddOrRemoveAll$ =  this.maxNumberOfSelections$.pipe(
			map(
				(maxNumberOfSelections) => {
					if (!maxNumberOfSelections) {
						return true;
					}

					return maxNumberOfSelections > 1;
				}
			),
			_logTap("showAddOrRemoveAll$"),
		);
		
		this.allowAddAll$ = this.selectableItems$.pipe(
			map(
				(selectableItems) => selectableItems.length >  0
			),
			_logTap("allowAddAll$")
		);
		
		this.allowRemoveAll$ =	this.selectedItems$.pipe(
			map(
				(selectedItems) => selectedItems.length > 0
			),
			_logTap("allowRemoveAll$")
		);

		this.showSelect$ = combineLatest([this.maxNumberOfSelections$, this.wrappedItems$]).pipe(
			map(
				(data) => {
					let [maxNumberOfSelections, wrappedItems] = data;
					
					// S'il n'y a plus d'objets selectionnables, masquer le champ <select>
					let nbSelectableItems = wrappedItems.filter($ => !$.selected).length;
					if (nbSelectableItems === 0) {
						return false;
					}
					
					// S'il n'y a pa de restrictions, afficher le champ <select>
					if (maxNumberOfSelections === null) {
						return true;
					}
					
					// S'il reste sinon des objets selectionables, on affiche le champ que si on a pas deja
					// selectionne le nombre maximum d'objets
					let nbSelectedItems = wrappedItems.filter($ => $.selected).length;
					return nbSelectedItems < maxNumberOfSelections;
				}
			),
			_logTap("showSelect$"),
		);
		
		///////////////////////////////////////
		// Reactions aux saisies utilisateur //
		///////////////////////////////////////
		
		unwrappedSelectedItems$.subscribe(
			(selectedItems) => {
				this.selectedItemsChange.emit(selectedItems);
			}
		);

		/////////////////////////////////////////////////
		// Reactions aux commandes de reinitialisation //
		/////////////////////////////////////////////////
		
		// A la demande formelle de reinitialisation venant du parent
		let reinitWrappedItemsEvent$ = this.reinitSubject.pipe(
			_logTap("reinitSubject"),
			// Ne rien faire s'il n'y a pas de donnee de reinitialisation
			filter(
				(data) => data != null && (data.list !== undefined || data.preselectionFunction != undefined)
			),
			withLatestFrom(this.wrappedItems$),
			map(
				(data) => {
					let [initData, wrappedItems] = data;
					
					if (initData === null) {
						throw new Error("Invalid state");
					}
					
					let items = initData.list;
					let preselectionFunction = initData.preselectionFunction;
					
					if (preselectionFunction === undefined) {
						// Si il n'y a pas de fonction de preselection, on essaie de preserver
						// les selections actuelles
						let selectedKeys = wrappedItems.map(this.keyGetter);
						preselectionFunction = (item: any) => selectedKeys.some($ => $ === this.keyGetter(item));
					}
					
					// S'il y a une nouvelle liste, on reinitiale les wrappedItems
					if (items !== undefined) {
						wrappedItems = items.map(($, index) => ({
							index: index,
							entity: $,
							selected: false,
						} as WrappedItem));
					}
					
					// Enfin, on aligne les selections a l'aide le fonction de preselection
					let pf = preselectionFunction;
					wrappedItems.forEach($ => $.selected = pf($.entity));

					return wrappedItems;
				}
			),
			_logTap("reinitWrappedItemsEvent$"),
		);
		
		// Quand on a definit qu'une reinitialisation etait necessaire et avec quelle liste, on l'applique
		reinitWrappedItemsEvent$.subscribe(
			(wrappedItems) => this.wrappedItems$.next(wrappedItems)
		)
	}
	
	@ContentChild(TemplateRef) notesTemplate!: TemplateRef<any>;

	@Input() id!: string;
	
	@Input() disabled = false;
	
	maxNumberOfSelections$ = new BehaviorSubject<number|null>(null);
	@Input() set maxNumberOfSelections(value: number | null) {
		this.maxNumberOfSelections$.next(value);
	}
	
	@Input() fieldClass: string = 'col-md-12';
	
	// La fonction a utiliser pour obtenir la clef d'un item
	@Input() keyGetter!: (item: any) => any;
 
	// La fonction a utiliser pour obtenir le libelle d'un item
	@Input() labelGetter!: (item: any) => string;

	// La liste des categories pour l'extended-filterable-select interne
	@Input() categoryList: any[] = []

	// La fonction a utiliser pour obtenir le libelle d'une categorie
	@Input() categoryLabelGetter!: (item: any) => string;

	// Le label de l'option "tous" dans la liste des categories (null = pas d'option "Tous")
	@Input() wildcardCategoryLabel: string | null = null;

	// La fonction a utiliser pour determiner si un item appartient a une categorie	
	@Input() categoryChecker!: (item: any, category: any) => boolean;
	
	private reinitSubject = new SubjectRelay<MultiExtendedFilterableSelect2ReinitData | null>();
	@Input() set reinitCommands(obs: Observable<MultiExtendedFilterableSelect2ReinitData | null> | null | undefined) {
		this.reinitSubject.registerSource(obs);
	}

	// Evenements de changement de selection
	@Output() selectedItemsChange = new EventEmitter<any[]>();

	onSelectItem(wrappedItem: WrappedItem | null) {
		this.selectedItem = wrappedItem;
		
		if (wrappedItem == null) {
			// Le composant de selection envoie null quand on deselectionne l'item dans celui-ci
			// Dans ce cas on ne fait rien ici
			return;
		}
		
		this.changeSelectionStatus(wrappedItem.index, true);
		this.resetSelectInput();
	}

	onDeselectItem(wrappedItem: WrappedItem) {
		this.changeSelectionStatus(wrappedItem.index, false);
	}
	
	private changeSelectionStatus(index: number, status: boolean) {
		if (!this.disabled) {
			let items = this.wrappedItems$.getValue();
			items[index].selected = status;
			this.wrappedItems$.next(items);
		}
	}

	onSelectAll() {
		this.reinitSelectionsTo(true);
	}

	onDeselectAll() {
		this.reinitSelectionsTo(false);
	}
	
	private reinitSelectionsTo(status: boolean) {
		if (!this.disabled) {
			let items = this.wrappedItems$.getValue();
			items.forEach($ => $.selected = status);
			this.wrappedItems$.next(items);
		}
	}

	private resetSelectInput() {
		this.selectedItem = -1;
		setTimeout(() => {
			this.selectedItem = null;
		}, 100);
	}

	doNothing() {
	}
}